/**
 * Language support for tickets and bookings (docs/screenshot-cleanup/PLAN.md, "Language support"): the one place that knows how
 * flights, trains and venue tickets word their parts in the languages Unpack supports. The reading copies every part exactly as shown,
 * in its own language; this vocabulary lets code read those parts (dates, times, seats, routes), confirm them against the check's lines,
 * and show the card's labels in English ("Einlass" as Doors, "Reihe 12" as Row 12). Values (names, codes, places) are never translated.
 *
 * Supported: English, German, French, Spanish and Italian, for booking parts only (dates and month names, clock times, time and
 * deadline labels, seat and place labels, routes). Anything else (another language, a label not listed, a post's or recipe's words) is
 * shown exactly as written, never guessed. To extend: add the words below; matching ignores case and accents.
 */
export const TICKET_LANGUAGES = ['en', 'de', 'fr', 'es', 'it'] as const

/** Lowercase without accents, so "Siège" and "siege", "Märzu" and "marz" match. */
export const plainWord = (text: string) => text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()

// Month names and abbreviations, by month (0-based).
const MONTH_WORDS: readonly (readonly string[])[] = [
  ['jan', 'january', 'januar', 'jaen', 'janner', 'janv', 'janvier', 'ene', 'enero', 'gen', 'gennaio'],
  ['feb', 'february', 'februar', 'fevr', 'fev', 'fevrier', 'febrero', 'febbraio'],
  ['mar', 'march', 'maerz', 'marz', 'mrz', 'mars', 'marzo'],
  ['apr', 'april', 'avr', 'avril', 'abr', 'abril', 'aprile'],
  ['may', 'mai', 'mayo', 'mag', 'maggio'],
  ['jun', 'june', 'juni', 'juin', 'junio', 'giu', 'giugno'],
  ['jul', 'july', 'juli', 'juil', 'juillet', 'julio', 'lug', 'luglio'],
  ['aug', 'august', 'aout', 'ago', 'agosto'],
  ['sep', 'sept', 'september', 'septembre', 'septiembre', 'set', 'sett', 'settembre'],
  ['oct', 'october', 'okt', 'oktober', 'octobre', 'octubre', 'ott', 'ottobre'],
  ['nov', 'november', 'novembre', 'noviembre'],
  ['dec', 'december', 'dez', 'dezember', 'decembre', 'dic', 'diciembre', 'dicembre'],
]
const MONTH_INDEX = new Map(MONTH_WORDS.flatMap((words, month) => words.map(word => [word, month] as [string, number])))

/** The month a word names, in any supported language ("Okt.", "octobre", "dic"), or null. */
export function monthOf(word: string): number | null {
  return MONTH_INDEX.get(plainWord(word).replace(/\.$/, '')) ?? null
}

// Weekday names (0 = Sunday) and the words for today and tomorrow, for a booking that names a day but no date ("Wednesday at 2:00 pm").
const WEEKDAY_WORDS: readonly (readonly string[])[] = [
  ['sunday', 'sonntag', 'dimanche', 'domingo', 'domenica'],
  ['monday', 'montag', 'lundi', 'lunes', 'lunedi'],
  ['tuesday', 'dienstag', 'mardi', 'martes', 'martedi'],
  ['wednesday', 'mittwoch', 'mercredi', 'miercoles', 'mercoledi'],
  ['thursday', 'donnerstag', 'jeudi', 'jueves', 'giovedi'],
  ['friday', 'freitag', 'vendredi', 'viernes', 'venerdi'],
  ['saturday', 'samstag', 'samedi', 'sabado', 'sabato'],
]
const WEEKDAY_INDEX = new Map(WEEKDAY_WORDS.flatMap((words, day) => words.map(word => [word, day] as [string, number])))
const TODAY_WORDS = new Set(['today', 'tonight', 'heute', 'aujourd hui', 'ce soir', 'hoy', 'esta noche', 'oggi', 'stasera'])
const TOMORROW_WORDS = new Set(['tomorrow', 'morgen', 'demain', 'manana', 'domani'])

/**
 * The day a booking means when it names only a weekday, today or tomorrow: days from the day the screenshot was taken (0 to 6), or null.
 * Only a whole word counts, and only full weekday names or today/tomorrow do: "Mi" or "So" alone are too common to read as days.
 */
export function relativeDay(text: string, capturedDay: number): number | null {
  const plain = plainWord(text).replace(/[’']/g, ' ')
  for (const word of TOMORROW_WORDS) if (new RegExp(`(?<!\\p{L})${word}(?!\\p{L})`, 'u').test(plain)) return 1
  for (const word of TODAY_WORDS) if (new RegExp(`(?<!\\p{L})${word.replace(' ', '\\s')}(?!\\p{L})`, 'u').test(plain)) return 0
  for (const word of plain.match(/\p{L}+/gu) ?? []) {
    const day = WEEKDAY_INDEX.get(word)
    if (day !== undefined) return (day - capturedDay + 7) % 7
  }
  return null
}

/** A month word as a pattern: letters with any accents, so the date reader can find it before the word is looked up. */
export const MONTH_PATTERN = String.raw`(\p{L}{3,10})\.?`

interface Term { en: string; words: readonly string[] }

// Labels of times and deadlines, shown in English on the card. Longer phrases first, so "gate closes" wins over "gate".
const TIME_TERMS: readonly Term[] = [
  { en: 'Gate closes', words: ['gate closes', 'gate closing', 'gate schliesst', 'gateschluss', 'fermeture de la porte', 'fermeture porte', 'fermeture embarquement', 'cierre de puerta', 'cierre de embarque', 'chiusura gate', 'chiusura imbarco'] },
  { en: 'Gate opens', words: ['gate opens', 'gate oeffnet', 'gate offnet', 'ouverture de la porte', 'apertura de puerta', 'apertura gate'] },
  { en: 'Boarding', words: ['boarding', 'boarding time', 'einstieg', 'einsteigen', 'embarquement', 'heure d embarquement', 'embarque', 'hora de embarque', 'imbarco', 'orario imbarco'] },
  { en: 'Check-in', words: ['check-in', 'check in', 'checkin', 'enregistrement', 'facturacion', 'accettazione'] },
  { en: 'Doors', words: ['doors', 'doors open', 'admission', 'einlass', 'turoffnung', 'tuoeffnung', 'ouverture des portes', 'ouverture', 'apertura de puertas', 'apertura puertas', 'apertura porte', 'apertura cancelli'] },
  { en: 'Departure', words: ['departure', 'departs', 'depart', 'abflug', 'abfahrt', 'salida', 'partenza'] },
  { en: 'Arrival', words: ['arrival', 'arrives', 'ankunft', 'arrivee', 'llegada', 'arrivo'] },
  { en: 'Start', words: ['start', 'starts', 'start time', 'begins', 'show', 'showtime', 'kick-off', 'kickoff', 'beginn', 'vorstellung', 'anpfiff', 'debut', 'spectacle', 'seance', 'coup d envoi', 'comienzo', 'inicio', 'funcion', 'sesion', 'inizio', 'spettacolo', 'calcio d inizio'] },
  { en: 'Ends', words: ['ends', 'end', 'ende', 'fin', 'fine'] },
  { en: 'Free cancellation until', words: ['free cancellation until', 'kostenlos stornierbar bis', 'kostenlose stornierung bis', 'annulation gratuite jusqu au', 'cancelacion gratuita hasta', 'cancellazione gratuita fino al'] },
]

/** The time labels that mark a booking's start (show, departure, check-in), in every supported language. */
export const START_TERMS = TIME_TERMS.filter(term => ['Start', 'Departure', 'Check-in'].includes(term.en)).flatMap(term => [term.en, ...term.words])

// Labels of a seat or place, shown in English in front of the value as written ("Reihe 12" as "Row 12").
const PLACE_TERMS: readonly Term[] = [
  { en: 'Boarding group', words: ['boarding group', 'boarding gruppe', 'groupe d embarquement', 'grupo de embarque', 'gruppo d imbarco', 'gruppo di imbarco'] },
  { en: 'Group', words: ['group', 'grp', 'gruppe', 'groupe', 'grupo', 'gruppo'] },
  { en: 'Zone', words: ['zone', 'zona'] },
  { en: 'Seat', words: ['seat', 'sitz', 'sitzplatz', 'platz', 'siege', 'place', 'asiento', 'butaca', 'localidad', 'posto', 'sedile', 'poltrona'] },
  { en: 'Row', words: ['row', 'reihe', 'rangee', 'rang', 'fila'] },
  { en: 'Block', words: ['block', 'bloc', 'bloque', 'blocco'] },
  { en: 'Section', words: ['section', 'sec', 'sektion', 'bereich', 'sector', 'seccion', 'settore'] },
  { en: 'Stand', words: ['stand', 'tribune', 'tribuene', 'tribuna', 'grada'] },
  { en: 'Level', words: ['level', 'tier', 'ebene', 'etage', 'niveau', 'nivel', 'planta', 'livello', 'anello'] },
  { en: 'Entrance', words: ['entrance', 'door', 'eingang', 'tor', 'entree', 'porte', 'entrada', 'acceso', 'puerta', 'ingresso', 'varco', 'porta'] },
  { en: 'Gate', words: ['gate', 'flugsteig', 'porte d embarquement', 'puerta de embarque', 'uscita', 'cancello'] },
  { en: 'Terminal', words: ['terminal', 'terminale'] },
  { en: 'Platform', words: ['platform', 'track', 'gleis', 'bahnsteig', 'voie', 'quai', 'anden', 'via', 'binario'] },
  { en: 'Coach', words: ['coach', 'car', 'carriage', 'wagen', 'voiture', 'coche', 'vagon', 'carrozza', 'vettura'] },
  { en: 'Cabin', words: ['cabin', 'kabine', 'cabine', 'camarote', 'cabina'] },
  { en: 'Deck', words: ['deck', 'pont', 'cubierta', 'ponte'] },
  { en: 'Table', words: ['table', 'tisch', 'mesa', 'tavolo'] },
  { en: 'Box', words: ['box', 'loge', 'palco'] },
]

// Words around a code that are not part of it, for confirming "Seat 14C" against a line showing "14C" ("Buchungsnummer HH-48213").
const REFERENCE_WORDS = ['order', 'booking', 'reference', 'ref', 'confirmation', 'conf', 'code', 'no', 'nr', 'number', 'pnr', 'record locator', 'ticket',
  'bestellung', 'bestellnummer', 'buchung', 'buchungsnummer', 'buchungscode', 'buchungsreferenz', 'auftragsnummer', 'nummer', 'commande', 'reservation',
  'reference de reservation', 'code de reservation', 'numero', 'billet', 'pedido', 'reserva', 'localizador', 'codigo', 'codigo de reserva', 'referencia',
  'entrada', 'ordine', 'prenotazione', 'codice', 'codice prenotazione', 'riferimento', 'biglietto', 'de', 'di', 'du']

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '[\\s\'’-]+')
const alternatives = (words: readonly string[]) => [...words].sort((a, b) => b.length - a.length).map(escape).join('|')

/** Every label word around a seat, place or reference, in every supported language, matched on plain (accentless) text. */
export const LABEL_WORDS = new RegExp(String.raw`(?<![\p{L}\p{N}])(?:${alternatives([...PLACE_TERMS.flatMap(term => [plainWord(term.en), ...term.words]), ...REFERENCE_WORDS])})(?![\p{L}\p{N}])\.?|#|n[°º]`, 'gu')

/**
 * The words between the two ends of a route ("BER to TLV", "Berlin → Tel Aviv", "Paris – Rome", "von Berlin nach Wien", "de Madrid a
 * Roma"): a route is confirmed end by end, since a boarding pass shows each end on its own line.
 */
export const ROUTE_SEPARATOR = /\s*(?:→|->|⇒|–|—|\s-\s|(?<!\p{L})(?:to|nach|vers|hacia|per|a|à)(?!\p{L}))\s*/iu

/** The labels of a route's two ends on their own lines ("From BER Terminal 1" / "To TLV", "Von Berlin" / "Nach Wien"). */
export const ROUTE_FROM = ['from', 'origin', 'von', 'ab', 'abflug von', 'de', 'depuis', 'origine', 'desde', 'origen', 'salida desde', 'da', 'partenza da']
export const ROUTE_TO = ['to', 'destination', 'nach', 'bis', 'ziel', 'a', 'vers', 'pour', 'hacia', 'destino', 'para', 'per', 'arrivo a']

/**
 * What a booking is for, from its own words in any supported language: a boarding pass or flight, a ride (taxi, Uber), a train ticket, a hotel stay. Shown
 * on the note's card in Browse ("Boarding pass", "BER → TLV, Sun 25 Oct"); anything else keeps its kind (Ticket, Reservation).
 *
 * Words are weighed by where they sit, because a long confirmation says "über" (about) and "Abholung" (collection) in passing:
 * - Name words are on the booking's own title, name and venue: they can say any purpose.
 * - Only unambiguous words ("taxi", "boarding pass", "Eurostar") count anywhere in the booking's text; ambiguous ones ("ride", "Bolt", "bahn") need to be in the name.
 * - An appointment (an office, a clinic, a registration) is never a journey or a stay: only a strong word in its name can say so.
 * - Brand names are matched as written, not as plain text, so "über" never reads as Uber.
 */
export type BookingPurpose = 'boarding-pass' | 'flight' | 'ride' | 'train' | 'hotel'
interface PurposeRule { purpose: BookingPurpose; strong: RegExp; weak?: RegExp; brand?: RegExp }
const PURPOSE_RULES: readonly PurposeRule[] = [
  { purpose: 'boarding-pass', strong: /\b(?:boarding ?pass|bordkarte|carte d.embarquement|tarjeta de embarque|pase de abordar|carta d.imbarco)\b/ },
  { purpose: 'flight', strong: /\b(?:flight|flug|vuelo|volo|airline|fluggesellschaft|compagnie aerienne|aerolinea|compagnia aerea)\b/, weak: /\bvol\b/ },
  // Before trains: a ride to the station names the station ("Hauptbahnhof", "Gare") without being a train ticket.
  { purpose: 'ride', strong: /\b(?:taxi|chauffeur|careem|fahrgast)\b/, brand: /\b(?:Uber|UBER|Lyft|LYFT|Free ?Now|FREE ?NOW)\b/,
    weak: /\b(?:cab|bolt|grab|ride|pick-?up|drop-?off|abholung|prise en charge|recogida|ritiro)\b/ },
  { purpose: 'train', strong: /\b(?:train|zug|tgv|sncf|ouigo|renfe|trenitalia|frecciarossa|italo|obb|sbb|eurostar|treno|gleis|binario)\b/, weak: /\b(?:bahn|tren|voie|anden)\b/ },
  { purpose: 'hotel', strong: /\b(?:hostel|check-?out|abreise|depart de l.hotel|salida del hotel|ferienwohnung|airbnb)\b/, weak: /\b(?:hotel|apartment)\b/ },
]
const FLIGHT_NUMBER = /^(?:[A-Z]{2}|[A-Z]\d|\d[A-Z])\s?\d{1,4}[A-Z]?$/

// An e-ticket receipt says what it is not ("This document is not your boarding pass", "Dieses Dokument ist nicht Ihre Bordkarte").
const NOT_A_PASS = /\b(?:not (?:your |a )?boarding ?pass|nicht ihre bordkarte|keine bordkarte|n.est pas (?:votre |une )?carte d.embarquement|no es (?:su |una )?tarjeta de embarque|non e (?:la (?:sua |vostra )?|una )?carta d.imbarco)\b/

/** The parts of a read item that say what a booking is for: its name words and its text. */
export interface PurposeInput { kind: string; title: string | null; purpose?: string | null; text: string; booking: { what: string | null; with: string | null } }

export function bookingPurpose(item: PurposeInput): BookingPurpose | null {
  const { what } = item.booking
  const named = [item.title, what, item.booking.with, item.purpose].filter(Boolean).join('\n')
  const appointment = item.kind === 'appointment'
  const anywhere = appointment ? named : `${named}\n${item.text}`
  const plainAnywhere = plainWord(anywhere)
  const plainNamed = plainWord(named)
  const found = PURPOSE_RULES.filter(rule => rule.purpose !== 'boarding-pass' || !NOT_A_PASS.test(plainAnywhere)).find(rule =>
    rule.strong.test(plainAnywhere) || rule.brand?.test(anywhere) || (!appointment && rule.weak?.test(plainNamed)))?.purpose ?? null
  return found ?? (!appointment && what && FLIGHT_NUMBER.test(what.trim()) ? 'flight' : null)
}

export const PURPOSE_LABEL: Record<BookingPurpose, string> = { 'boarding-pass': 'Boarding pass', flight: 'Flight', ride: 'Taxi ride', train: 'Train ticket', hotel: 'Hotel stay' }

/** A route's two ends ("BER Terminal 1 to TLV" → BER Terminal 1, TLV), or null when it is not one. */
export function routeEnds(where: string | null): [string, string] | null {
  const parts = where?.split(ROUTE_SEPARATOR).map(part => part.trim()).filter(Boolean) ?? []
  return parts.length === 2 ? [parts[0]!, parts[1]!] : null
}

function termFor(terms: readonly Term[], text: string): Term | null {
  const plain = plainWord(text).replace(/[’']/g, ' ').replace(/\s+/g, ' ').trim()
  return terms.find(term => plainWord(term.en) === plain || term.words.includes(plain)) ?? null
}

/** A time or deadline label in English when it is one this vocabulary knows ("Einlass" → "Doors"); otherwise as written. */
export function englishTimeLabel(label: string | null): string | null {
  if (!label) return label
  return termFor(TIME_TERMS, label.replace(/[:.]+$/, ''))?.en ?? label
}

/** A seat or place split into its English label and its value ("Reihe 12" → Row, 12); no label when it has none this vocabulary knows. */
export function placeParts(entry: string): { label: string | null; value: string } {
  const match = /^(\p{L}[\p{L}\s'’.-]*?)\s*[:#.]?\s*([\p{N}\p{Lu}][\p{L}\p{N}\s/-]*)$/u.exec(entry.trim())
  const term = match ? termFor(PLACE_TERMS, match[1]!.replace(/\.$/, '')) : null
  return term ? { label: term.en, value: match![2]!.trim() } : { label: null, value: entry.trim() }
}

/** A seat or place in English when its label is one this vocabulary knows ("Reihe 12" → "Row 12", "Posto: 7" → "Seat 7"); else as written. */
export function englishPlace(entry: string): string {
  const parts = placeParts(entry)
  return parts.label ? `${parts.label} ${parts.value}` : entry
}
